import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Users, Calendar, AlertTriangle, Loader2,
  Clock, TrendingUp, TrendingDown, BarChart3, UserCheck, Briefcase, MapPin, DollarSign,
  Settings, Eye, EyeOff, ChevronLeft, ChevronRight
} from 'lucide-react';
import { formatTime, formatTimeRange } from '../utils/time';
import logger from '../utils/logger';
import DailyPagesWidget from '../components/DailyPagesWidget';

const SERVICE_LABELS = { day_program: 'Explore Program', in_home: 'IHCS', employment: 'Employment', private_pay: 'Private Pay' };
const SERVICE_COLORS = {
  day_program: { bg: 'bg-[#EDE7F6]', text: 'text-[#6B3FA0]', border: 'border-[#9B7ED8]', bar: 'bg-[#9B7ED8]' },
  in_home: { bg: 'bg-[#E8F5E9]', text: 'text-[#1B5E20]', border: 'border-[#81C784]', bar: 'bg-[#81C784]' },
  employment: { bg: 'bg-[#FFF3E0]', text: 'text-[#E65100]', border: 'border-[#FFB74D]', bar: 'bg-[#FFB74D]' },
  private_pay: { bg: 'bg-[#E1F5FE]', text: 'text-[#01579B]', border: 'border-[#4FC3F7]', bar: 'bg-[#4FC3F7]' },
};

function UtilizationBar({ percent, size = 'md' }) {
  const h = size === 'sm' ? 'h-1.5' : 'h-2.5';
  const color = percent >= 85 ? 'bg-[#D9534F]' : percent >= 60 ? 'bg-[#6B3FA0]' : percent >= 30 ? 'bg-[#FFB74D]' : 'bg-[#DED8EB]';
  return (
    <div className={`w-full ${h} bg-[#F0EDF6] rounded-full overflow-hidden`}>
      <div className={`${h} ${color} rounded-full transition-all duration-500`} style={{ width: `${Math.min(percent, 100)}%` }} />
    </div>
  );
}

function StaffHoursWidget({ data }) {
  if (!data) return null;
  const { summary, staff } = data;
  const atCap = staff.filter(s => s.at_cap);
  const nearCap = staff.filter(s => s.near_cap && !s.at_cap);
  const underUtilized = staff.filter(s => s.under_utilized && !s.at_cap);

  return (
    <Card className="border-[#DED8EB] shadow-sm bg-white" data-testid="staff-hours-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Clock className="h-4 w-4 text-[#6B3FA0]" /> Staff Hours This Week
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Overall utilization */}
        <div className="p-3 rounded-lg bg-[#F0EDF6] border border-[#DED8EB]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-[#5A4D6E] uppercase tracking-wider">Team Utilization</span>
            <span className="text-xl font-bold text-[#2D1B4E]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{summary.overall_utilization}%</span>
          </div>
          <UtilizationBar percent={summary.overall_utilization} />
          <p className="text-[10px] text-[#8E85A0] mt-1.5" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
            {summary.total_worked_hours}h / {summary.total_capacity_hours}h capacity
          </p>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-2">
          <div className="text-center p-2 rounded-lg bg-red-50 border border-red-100">
            <p className="text-lg font-bold text-[#D9534F]" style={{ fontFamily: 'Manrope, sans-serif' }}>{summary.at_capacity}</p>
            <p className="text-[10px] text-[#D9534F] font-medium">At Cap</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-[#FFF3E0] border border-[#FFB74D]/30">
            <p className="text-lg font-bold text-[#E65100]" style={{ fontFamily: 'Manrope, sans-serif' }}>{summary.near_capacity}</p>
            <p className="text-[10px] text-[#E65100] font-medium">Near Cap</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-[#EDE7F6] border border-[#9B7ED8]/30">
            <p className="text-lg font-bold text-[#6B3FA0]" style={{ fontFamily: 'Manrope, sans-serif' }}>{summary.under_utilized}</p>
            <p className="text-[10px] text-[#6B3FA0] font-medium">Available</p>
          </div>
        </div>

        {/* Staff list */}
        <ScrollArea className="h-56">
          <div className="space-y-1.5 pr-2">
            {staff.map(s => (
              <div key={s.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#F7F4EF] transition-colors" data-testid={`staff-hours-${s.id}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0 ${s.at_cap ? 'bg-[#D9534F]' : s.near_cap ? 'bg-[#FFB74D]' : 'bg-[#9B7ED8]'}`}>
                  {s.name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-[#2D1B4E] truncate">{s.name}</p>
                    <span className="text-[10px] font-mono shrink-0 text-[#8E85A0]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                      {s.weekly_hours}/{s.max_weekly_hours}h
                    </span>
                  </div>
                  <UtilizationBar percent={s.utilization} size="sm" />
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

function AvailabilityWidget({ data }) {
  if (!data) return null;
  const available = data.staff.filter(s => !s.at_cap && s.remaining > 0).sort((a, b) => b.remaining - a.remaining);

  return (
    <Card className="border-[#DED8EB] shadow-sm bg-white" data-testid="availability-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <UserCheck className="h-4 w-4 text-[#2E7D32]" /> Available Staff
        </CardTitle>
        <p className="text-xs text-[#8E85A0]">Staff with hours remaining this week</p>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-72">
          <div className="space-y-1 pr-2">
            {available.map(s => (
              <div key={s.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-transparent hover:border-[#DED8EB] hover:bg-[#F7F4EF] transition-all" data-testid={`available-${s.id}`}>
                <div className="w-8 h-8 rounded-full bg-[#E8F5E9] flex items-center justify-center text-[#2E7D32] text-xs font-bold shrink-0">
                  {s.name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#2D1B4E] truncate">{s.name}</p>
                  <p className="text-[10px] text-[#8E85A0]">{s.role}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-[#2E7D32]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>+{s.remaining}h</p>
                  <p className="text-[10px] text-[#8E85A0]">{s.utilization}% used</p>
                </div>
              </div>
            ))}
            {available.length === 0 && (
              <div className="text-center py-6 text-[#8E85A0]">
                <Users className="h-6 w-6 mx-auto mb-1 opacity-50" />
                <p className="text-sm">All staff at capacity</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

function ServiceCoverageWidget({ data }) {
  const totals = useMemo(() => {
    if (!data?.coverage) return { shifts: 0, assigned: 0, unassigned: 0 };
    const values = Object.values(data.coverage);
    return {
      shifts: values.reduce((s, c) => s + c.total_shifts, 0),
      assigned: values.reduce((s, c) => s + c.assigned, 0),
      unassigned: values.reduce((s, c) => s + c.unassigned, 0),
    };
  }, [data]);

  if (!data) return null;
  const { coverage } = data;

  return (
    <Card className="border-[#DED8EB] shadow-sm bg-white" data-testid="service-coverage-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <BarChart3 className="h-4 w-4 text-[#E65100]" /> Service Coverage This Week
        </CardTitle>
        <p className="text-xs text-[#8E85A0]">Staffing levels by service line</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {Object.entries(coverage).map(([sl, c]) => {
          const colors = SERVICE_COLORS[sl];
          const noShifts = c.fill_rate === null || c.fill_rate === undefined;
          const needsAttention = c.unassigned > 0;
          return (
            <div key={sl} className={`p-3 rounded-lg border ${noShifts ? 'border-[#DED8EB] bg-[#F7F4EF]' : needsAttention ? 'border-[#FFB74D] bg-[#FFF3E0]/30' : `${colors.border} ${colors.bg}/30`}`} data-testid={`coverage-${sl}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-semibold ${colors.text}`}>{SERVICE_LABELS[sl]}</span>
                  {needsAttention && <Badge className="text-[9px] bg-[#FFB74D] text-white">Needs Staff</Badge>}
                  {noShifts && <Badge className="text-[9px] bg-gray-200 text-gray-600">No shifts</Badge>}
                </div>
                <span className="text-sm font-bold text-[#2D1B4E]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                  {noShifts ? '—' : `${c.fill_rate}%`}
                </span>
              </div>
              {!noShifts && <UtilizationBar percent={c.fill_rate} size="sm" />}
              <div className="flex items-center gap-4 mt-2 text-xs text-[#5A4D6E]">
                <span className="flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full ${colors.bar}`} />
                  {c.assigned} assigned
                </span>
                {c.unassigned > 0 && (
                  <span className="flex items-center gap-1 text-[#E65100] font-semibold">
                    <AlertTriangle className="h-3 w-3" /> {c.unassigned} open
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" /> {c.unique_participants} participants
                </span>
              </div>
            </div>
          );
        })}

        {/* Total summary */}
        <div className="p-3 rounded-lg bg-[#F7F4EF] border border-[#DED8EB]">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-[#2D1B4E]">Total This Week</span>
            <span className="text-sm font-bold text-[#2D1B4E]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
              {totals.shifts} shifts
            </span>
          </div>
          <div className="flex items-center gap-4 mt-1 text-xs text-[#5A4D6E]">
            <span>{totals.assigned} staffed</span>
            {totals.unassigned > 0 && (
              <span className="text-[#E65100] font-semibold">
                {totals.unassigned} need staff
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BillingProjectionWidget({ api, weekOffset }) {
  const [view, setView] = React.useState('weekly');
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const fetchBilling = React.useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      const ws = new Date(now);
      ws.setDate(ws.getDate() - ws.getDay() + weekOffset * 7);
      const weekStart = ws.toISOString().split('T')[0];

      // Month derived from the displayed week (the month containing the week start).
      const month = `${ws.getFullYear()}-${String(ws.getMonth() + 1).padStart(2, '0')}`;

      const { data: d } = await api.get(`/dashboard/billing?week_start=${weekStart}&month=${month}`);
      setData(d);
    } catch {}
    setLoading(false);
  }, [api, weekOffset]);

  React.useEffect(() => { fetchBilling(); }, [fetchBilling]);

  if (!data || loading) return (
    <Card className="border-[#DED8EB] shadow-sm bg-white lg:col-span-2" data-testid="billing-widget">
      <CardContent className="p-8 text-center text-[#8E85A0]"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></CardContent>
    </Card>
  );

  const current = view === 'weekly' ? data.weekly : data.monthly;

  const periodLabel = view === 'weekly'
    ? (() => {
        const ws = new Date(data.week_start + 'T12:00:00');
        const we = new Date(ws); we.setDate(we.getDate() + 6);
        return `${ws.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${we.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      })()
    : (() => {
        const [y, m] = data.month.split('-');
        return new Date(y, m - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      })();

  const services = [
    { key: 'day_program', label: 'Explore Program', rate: `$${data.rates?.day_program || '6.32'}`, color: 'bg-[#9B7ED8]', textColor: 'text-[#6B3FA0]' },
    { key: 'employment', label: 'Employment', rate: `$${data.rates?.employment || '20.60'}`, color: 'bg-[#FFB74D]', textColor: 'text-[#E65100]' },
    { key: 'in_home', label: 'IHCS', rate: `$${data.rates?.in_home || '11.10'}`, color: 'bg-[#81C784]', textColor: 'text-[#1B5E20]' },
    { key: 'private_pay', label: 'Private Pay', rate: `$${data.rates?.private_pay || '0.00'}`, color: 'bg-[#4FC3F7]', textColor: 'text-[#01579B]' },
  ];
  const maxAmount = Math.max(...services.map(s => current.by_service[s.key]?.amount || 0), 1);

  return (
    <Card className="border-[#DED8EB] shadow-sm bg-white lg:col-span-2" data-testid="billing-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <DollarSign className="h-4 w-4 text-[#2E7D32]" /> Billable Revenue
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-[#F0EDF6] border border-[#DED8EB] rounded-lg p-0.5">
              <button onClick={() => setView('weekly')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${view === 'weekly' ? 'bg-white text-[#2D1B4E] shadow-sm' : 'text-[#8E85A0]'}`}
                data-testid="billing-weekly-btn">Week</button>
              <button onClick={() => setView('monthly')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${view === 'monthly' ? 'bg-white text-[#2D1B4E] shadow-sm' : 'text-[#8E85A0]'}`}
                data-testid="billing-monthly-btn">Month</button>
            </div>
          </div>
        </div>
        <p className="text-xs font-semibold text-[#2D1B4E] mt-1">{periodLabel}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Total */}
        <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-[#2D1B4E] to-[#6B3FA0]">
          <div>
            <p className="text-xs text-white/60 uppercase tracking-wider font-semibold">Projected Revenue</p>
            <p className="text-3xl font-extrabold text-white mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
              ${current.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-white/80 font-semibold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{current.total_units.toLocaleString()} units</p>
            <p className="text-xs text-white/50">{current.total_hours}h scheduled</p>
          </div>
        </div>

        {/* Service breakdown */}
        <div className="space-y-3">
          {services.map(({ key, label: sLabel, rate, color, textColor, bgLight }) => {
            const svc = current.by_service[key] || {};
            const pct = maxAmount > 0 ? (svc.amount / maxAmount) * 100 : 0;
            return (
              <div key={key} className="space-y-1.5" data-testid={`billing-${key}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-sm ${color}`} />
                    <span className="text-sm font-semibold text-[#2D1B4E]">{sLabel}</span>
                    <span className="text-[10px] text-[#8E85A0]">{rate}/unit</span>
                  </div>
                  <span className={`text-sm font-bold ${textColor}`} style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                    ${svc.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-[#F0EDF6] rounded-full overflow-hidden">
                    <div className={`h-2 ${color} rounded-full transition-all duration-500`} style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                  <span className="text-[10px] text-[#8E85A0] w-20 text-right" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                    {svc.units?.toLocaleString() || 0} units
                  </span>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-[#8E85A0]">
                  <span>{svc.shifts || 0} shifts</span>
                  <span>{svc.hours || 0}h</span>
                  {key === 'day_program' && svc.participants > 0 && <span>{svc.participants} participant-slots</span>}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function StaffWeeklyTotalsWidget({ api, weekOffset }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      const ws = new Date(now);
      ws.setDate(ws.getDate() - ws.getDay() + weekOffset * 7);
      const weekStart = ws.toISOString().split('T')[0];
      const { data: d } = await api.get(`/dashboard/staff-weekly-totals?week_start=${weekStart}`);
      setData(d);
    } catch {}
    setLoading(false);
  }, [api, weekOffset]);

  React.useEffect(() => { fetchData(); }, [fetchData]);

  if (!data || loading) return (
    <Card className="border-[#DED8EB] shadow-sm bg-white lg:col-span-2" data-testid="staff-totals-widget">
      <CardContent className="p-8 text-center text-[#8E85A0]"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></CardContent>
    </Card>
  );

  const ws = new Date(data.week_start + 'T12:00:00');
  const we = new Date(data.week_end + 'T12:00:00');
  const periodLabel = `${ws.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${we.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  const staffWithHours = data.staff.filter(s => s.hours > 0);
  const maxHours = Math.max(...data.staff.map(s => s.cap), 1);

  return (
    <Card className="border-[#DED8EB] shadow-sm bg-white lg:col-span-2" data-testid="staff-totals-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Users className="h-4 w-4 text-[#6B3FA0]" /> Staff Weekly Totals
          </CardTitle>
          <div className="text-right">
            <p className="text-xs text-[#8E85A0]">{staffWithHours.length} staff scheduled</p>
          </div>
        </div>
        <p className="text-xs font-semibold text-[#2D1B4E] mt-1">{periodLabel}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Summary bar */}
        <div className="flex items-center justify-between p-3 rounded-xl bg-[#F0EDF6] border border-[#DED8EB]">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-2xl font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{data.total_hours}h</p>
              <p className="text-[10px] text-[#8E85A0] uppercase tracking-wider">Total Hours</p>
            </div>
            <div className="h-8 w-px bg-[#DED8EB]" />
            <div>
              <p className="text-lg font-bold text-[#5A4D6E]">{data.total_capacity}h</p>
              <p className="text-[10px] text-[#8E85A0] uppercase tracking-wider">Capacity</p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-lg font-bold ${data.overall_pct > 85 ? 'text-[#E65100]' : data.overall_pct > 50 ? 'text-[#6B3FA0]' : 'text-[#8E85A0]'}`}>{data.overall_pct}%</p>
            <p className="text-[10px] text-[#8E85A0]">Utilization</p>
          </div>
        </div>

        {/* Staff list */}
        <div className="max-h-[320px] overflow-y-auto space-y-1.5 pr-1">
          {data.staff.map(s => {
            const barWidth = maxHours > 0 ? (s.hours / maxHours) * 100 : 0;
            const isOver = s.hours > s.cap;
            const isNear = s.pct >= 85 && !isOver;
            return (
              <div key={s.id} className="flex items-center gap-2 group" data-testid={`totals-${s.id}`}>
                <div className="w-28 shrink-0 truncate">
                  <p className="text-xs font-medium text-[#2D1B4E] truncate">{s.name}</p>
                </div>
                <div className="flex-1 h-5 bg-[#F0EDF6] rounded-full overflow-hidden relative">
                  <div className={`h-5 rounded-full transition-all duration-500 ${isOver ? 'bg-[#D9534F]' : isNear ? 'bg-[#FFB74D]' : 'bg-[#9B7ED8]'}`}
                    style={{ width: `${Math.min(barWidth, 100)}%` }} />
                  {s.cap > 0 && (
                    <div className="absolute top-0 h-5 w-px bg-[#2D1B4E]/30" style={{ left: `${(s.cap / maxHours) * 100}%` }} title={`Cap: ${s.cap}h`} />
                  )}
                </div>
                <div className="w-20 shrink-0 text-right">
                  <span className={`text-xs font-bold ${isOver ? 'text-[#D9534F]' : isNear ? 'text-[#E65100]' : 'text-[#2D1B4E]'}`} style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                    {s.hours}/{s.cap}h
                  </span>
                </div>
                <span className="text-[10px] text-[#8E85A0] w-8 text-right shrink-0">{s.days_worked}d</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function WeeklyGapsWidget({ api, weekOffset }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const computeWeekStart = useCallback((offset) => {
    const now = new Date();
    const dow = now.getDay();
    const sunday = new Date(now); sunday.setDate(now.getDate() - dow + (offset * 7));
    return sunday.toISOString().split('T')[0];
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ws = computeWeekStart(weekOffset);
      const { data } = await api.get('/dashboard/weekly-gaps', { params: { week_start: ws } });
      setData(data);
    } catch (e) { logger.error('Weekly gaps fetch error:', e); }
    finally { setLoading(false); }
  }, [api, weekOffset, computeWeekStart]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <Card className="border-[#DED8EB] shadow-sm bg-white lg:col-span-3" data-testid="weekly-gaps-widget">
        <CardContent className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#6B3FA0]" /></CardContent>
      </Card>
    );
  }

  const SERVICES = [
    { key: 'day_program', label: 'Explore' },
    { key: 'employment', label: 'Employment' },
    { key: 'in_home', label: 'IHCS' },
    { key: 'private_pay', label: 'Private Pay' },
  ];
  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const totalGap = data.overall.gap;
  const totalNeeded = data.overall.needed;
  const fillRate = totalNeeded > 0 ? Math.round(((totalNeeded - totalGap) / totalNeeded) * 100) : 100;

  return (
    <Card className="border-[#DED8EB] shadow-sm bg-white lg:col-span-3" data-testid="weekly-gaps-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-xl font-semibold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <AlertTriangle className={`h-5 w-5 ${totalGap > 0 ? 'text-[#D9534F]' : 'text-[#2E7D32]'}`} />
              Weekly Staffing Gaps
            </CardTitle>
            <p className="text-xs text-[#8E85A0] mt-0.5">
              {fmtGapsRange(data.week_start, data.week_end)} · {totalGap > 0 ? <span className="text-[#D9534F] font-semibold">{totalGap} unfilled slot{totalGap===1?'':'s'} this week</span> : <span className="text-[#2E7D32] font-semibold">Fully staffed ✓</span>} · {fillRate}% filled
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#8E85A0]">
                <th className="text-left pb-2 pr-3 font-semibold">Service</th>
                {data.days.map((d, i) => (
                  <th key={d.date} className="text-center pb-2 px-1 font-semibold">
                    <div>{dayLabels[i]}</div>
                    <div className="text-[#5A4D6E]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{d.date.slice(5)}</div>
                  </th>
                ))}
                <th className="text-center pb-2 pl-3 font-semibold">Wk Total</th>
              </tr>
            </thead>
            <tbody>
              {SERVICES.map(svc => {
                const colors = SERVICE_COLORS[svc.key];
                const t = data.totals_by_service[svc.key];
                return (
                  <tr key={svc.key} className="border-t border-[#F0EDF6]">
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-sm ${colors.bar}`} />
                        <span className="text-sm font-semibold text-[#2D1B4E]">{svc.label}</span>
                      </div>
                    </td>
                    {data.days.map(d => <GapCell key={d.date} svc={d.services[svc.key]} />)}
                    <td className="text-center pl-3 py-2">
                      <div className={`text-sm font-bold ${t.gap > 0 ? 'text-[#D9534F]' : 'text-[#2E7D32]'}`} style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                        {t.filled}<span className="text-[#8E85A0] font-normal mx-0.5">/</span>{t.needed}
                      </div>
                      {t.gap > 0 && <div className="text-[10px] text-[#D9534F] font-semibold">−{t.gap}</div>}
                      {t.over > 0 && <div className="text-[10px] text-[#1565C0] font-semibold">+{t.over}</div>}
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t-2 border-[#DED8EB] bg-[#FAF8FE]">
                <td className="py-2 pr-3 text-xs font-bold text-[#5A4D6E] uppercase tracking-wider">Day total</td>
                {data.days.map(d => {
                  const dayTotal = SERVICES.reduce((acc, s) => ({
                    gap: acc.gap + d.services[s.key].gap,
                    needed: acc.needed + d.services[s.key].needed,
                    filled: acc.filled + d.services[s.key].filled,
                  }), { gap: 0, needed: 0, filled: 0 });
                  return (
                    <td key={d.date} className="text-center py-2 px-1">
                      {dayTotal.needed === 0 ? (
                        <span className="text-[10px] text-[#8E85A0]">—</span>
                      ) : (
                        <>
                          <div className={`text-xs font-bold ${dayTotal.gap > 0 ? 'text-[#D9534F]' : 'text-[#2E7D32]'}`} style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                            {dayTotal.filled}/{dayTotal.needed}
                          </div>
                          {dayTotal.gap > 0 && <div className="text-[9px] text-[#D9534F] font-semibold">−{dayTotal.gap}</div>}
                        </>
                      )}
                    </td>
                  );
                })}
                <td className="text-center pl-3 py-2">
                  <div className={`text-base font-extrabold ${totalGap > 0 ? 'text-[#D9534F]' : 'text-[#2E7D32]'}`} style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                    {data.overall.filled}/{data.overall.needed}
                  </div>
                  {totalGap > 0 && <div className="text-xs text-[#D9534F] font-bold">−{totalGap}</div>}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-[#F0EDF6] text-[10px] text-[#8E85A0] flex-wrap">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#FEE2E2] border border-[#FCA5A5]" />Gap</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#FEF3C7] border border-[#FCD34D]" />Partial</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#D1FAE5] border border-[#6EE7B7]" />Filled</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#DBEAFE] border border-[#93C5FD]" />Over</span>
          <span className="flex items-center gap-1.5 ml-auto">Format: <span className="font-mono text-[#2D1B4E]">filled/needed</span></span>
        </div>
      </CardContent>
    </Card>
  );
}

function GapCell({ svc }) {
  if (!svc || svc.needed === 0) {
    return <td className="text-center py-2 px-1 text-[10px] text-[#D6D0E5]">—</td>;
  }
  let bg = 'bg-[#D1FAE5] border-[#6EE7B7]';
  let textColor = 'text-[#065F46]';
  if (svc.gap > 0) {
    if (svc.filled === 0) { bg = 'bg-[#FEE2E2] border-[#FCA5A5]'; textColor = 'text-[#991B1B]'; }
    else { bg = 'bg-[#FEF3C7] border-[#FCD34D]'; textColor = 'text-[#92400E]'; }
  } else if (svc.over > 0) {
    bg = 'bg-[#DBEAFE] border-[#93C5FD]'; textColor = 'text-[#1E40AF]';
  }
  return (
    <td className="text-center py-1 px-1">
      <div
        className={`inline-block px-2 py-1 rounded border ${bg} min-w-[44px]`}
        title={`Needed ${svc.needed} · Filled ${svc.filled} · Gap ${svc.gap}${svc.over ? ` · Over ${svc.over}` : ''}${svc.unassigned_slots ? ` · ${svc.unassigned_slots} unassigned slot(s)` : ''}`}
      >
        <span className={`text-xs font-bold ${textColor}`} style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
          {svc.filled}<span className="opacity-50 mx-0.5">/</span>{svc.needed}
        </span>
        {svc.gap > 0 && <div className={`text-[9px] font-bold ${textColor}`}>−{svc.gap}</div>}
        {svc.over > 0 && <div className={`text-[9px] font-bold ${textColor}`}>+{svc.over}</div>}
      </div>
    </td>
  );
}

function fmtGapsRange(start, end) {
  try {
    const s = new Date(start + 'T00:00:00');
    const e = new Date(end + 'T00:00:00');
    const sm = s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const em = e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${sm} – ${em}`;
  } catch { return `${start} – ${end}`; }
}

export default function AdminDashboard() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [todayShifts, setTodayShifts] = useState([]);
  const [staffUtil, setStaffUtil] = useState(null);
  const [serviceCov, setServiceCov] = useState(null);
  const [dashPrefs, setDashPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWidgetManager, setShowWidgetManager] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);

  // Compute the human-readable label for the global week cursor.
  const cursorLabel = useMemo(() => {
    const now = new Date();
    const ws = new Date(now);
    ws.setDate(ws.getDate() - ws.getDay() + weekOffset * 7);
    const we = new Date(ws); we.setDate(we.getDate() + 6);
    return `${ws.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${we.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }, [weekOffset]);

  const WIDGET_DEFS = [
    { id: 'stats', label: 'Stat Cards' },
    { id: 'staff_hours', label: 'Staff Hours' },
    { id: 'availability', label: 'Available Staff' },
    { id: 'coverage', label: 'Service Coverage' },
    { id: 'gaps', label: 'Weekly Gaps' },
    { id: 'billing', label: 'Billing Projection' },
    { id: 'rates', label: 'Unit Rates' },
    { id: 'staff_totals', label: 'Staff Weekly Totals' },
    { id: 'today_schedule', label: "Today's Schedule" },
  ];

  const fetchDashboard = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const [statsRes, shiftsRes, utilRes, covRes, prefsRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get(`/shifts?date=${today}`),
        api.get('/dashboard/staff-utilization'),
        api.get('/dashboard/service-coverage'),
        api.get('/settings/dashboard-prefs'),
      ]);
      setStats(statsRes.data);
      setTodayShifts(shiftsRes.data);
      setStaffUtil(utilRes.data);
      setServiceCov(covRes.data);
      setDashPrefs(prefsRes.data);
    } catch (e) { logger.error('Dashboard fetch error:', e); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const sortedTodayShifts = useMemo(
    () => [...todayShifts].sort((a, b) => a.time_in.localeCompare(b.time_in)),
    [todayShifts]
  );

  const hiddenWidgets = new Set(dashPrefs?.hidden || []);
  const isVisible = (id) => !hiddenWidgets.has(id);
  const toggleWidget = async (id) => {
    const newHidden = hiddenWidgets.has(id) ? [...hiddenWidgets].filter(h => h !== id) : [...hiddenWidgets, id];
    setDashPrefs(p => ({ ...p, hidden: newHidden }));
    try { await api.put('/settings/dashboard-prefs', { widgets: dashPrefs?.widgets || [], hidden: newHidden }); } catch {}
  };

  if (loading) {
    return (
      <div className="p-8 animate-pulse space-y-6">
        <div className="h-8 w-48 bg-[#DED8EB] rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-28 bg-[#DED8EB] rounded-lg" />)}
        </div>
      </div>
    );
  }

  const statCards = [
    { label: 'Active Staff', value: stats?.active_employees || 0, icon: Users, color: 'text-[#6B3FA0]', bg: 'bg-[#EDE7F6]' },
    { label: "Today's Shifts", value: stats?.today_shifts || 0, icon: Calendar, color: 'text-[#1B5E20]', bg: 'bg-[#E8F5E9]' },
    { label: 'Team Utilization', value: `${staffUtil?.summary?.overall_utilization || 0}%`, icon: TrendingUp, color: 'text-[#6B3FA0]', bg: 'bg-[#EDE7F6]' },
    { label: 'Conflicts Today', value: stats?.conflicts_today || 0, icon: AlertTriangle, color: stats?.conflicts_today > 0 ? 'text-[#D9534F]' : 'text-[#2E7D32]', bg: stats?.conflicts_today > 0 ? 'bg-red-50' : 'bg-green-50' },
  ];

  return (
    <div className="p-4 sm:p-8 space-y-8" data-testid="admin-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl tracking-tight font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>Dashboard</h1>
          <p className="text-base text-[#5A4D6E] mt-1">Overview of today's scheduling activity</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Global week cursor — drives Billing, Staff Totals, and Weekly Gaps widgets in lockstep. */}
          <div className="flex items-center gap-1 bg-white border border-[#DED8EB] rounded-lg px-1 py-1 shadow-sm" data-testid="dashboard-week-cursor">
            <Button variant="ghost" size="icon" onClick={() => setWeekOffset(o => o - 1)} className="h-7 w-7 text-[#5A4D6E] hover:text-[#6B3FA0] hover:bg-[#EDE7F6]" data-testid="cursor-prev" title="Previous week">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="px-2 min-w-[170px] text-center">
              <p className="text-[10px] text-[#8E85A0] uppercase tracking-wider leading-tight">{weekOffset === 0 ? 'This week' : weekOffset > 0 ? `+${weekOffset} ${weekOffset === 1 ? 'week' : 'weeks'}` : `${weekOffset} ${weekOffset === -1 ? 'week' : 'weeks'}`}</p>
              <p className="text-xs font-bold text-[#2D1B4E] leading-tight" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="cursor-label">{cursorLabel}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setWeekOffset(o => o + 1)} className="h-7 w-7 text-[#5A4D6E] hover:text-[#6B3FA0] hover:bg-[#EDE7F6]" data-testid="cursor-next" title="Next week">
              <ChevronRight className="h-4 w-4" />
            </Button>
            {weekOffset !== 0 && (
              <Button variant="ghost" size="sm" onClick={() => setWeekOffset(0)} className="h-7 px-2 text-[10px] text-[#6B3FA0] hover:bg-[#EDE7F6]" data-testid="cursor-today">Today</Button>
            )}
          </div>
          <Button variant="outline" onClick={() => setShowWidgetManager(!showWidgetManager)}
            className="border-[#DED8EB] text-[#5A4D6E] hover:bg-[#EDE7F6] gap-2" data-testid="widget-manager-btn">
            <Settings className="h-4 w-4" /> Customize
          </Button>
        </div>
      </div>

      {/* Daily Pages — solo writing nudge for users with book_access */}
      <DailyPagesWidget />

      {/* Widget Manager */}
      {showWidgetManager && (
        <Card className="border-[#9B7ED8] bg-[#EDE7F6]/50 shadow-sm" data-testid="widget-manager">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-[#2D1B4E]">Dashboard Widgets — toggle visibility</p>
              <button onClick={() => setShowWidgetManager(false)} className="text-xs text-[#8E85A0] hover:text-[#6B3FA0]">Close</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {WIDGET_DEFS.map(w => (
                <button key={w.id} onClick={() => toggleWidget(w.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                    isVisible(w.id) ? 'bg-white border-[#6B3FA0] text-[#6B3FA0]' : 'bg-white/50 border-[#DED8EB] text-[#8E85A0]'
                  }`} data-testid={`toggle-widget-${w.id}`}>
                  {isVisible(w.id) ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                  {w.label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stat cards */}
      {isVisible('stats') && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
          {statCards.map(({ label, value, icon: Icon, color, bg }) => (
            <Card key={label} className="border-[#DED8EB] shadow-sm bg-white" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#8E85A0]">{label}</p>
                    <p className={`text-3xl font-extrabold mt-1 ${color}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
                  </div>
                  <div className={`w-12 h-12 ${bg} rounded-lg flex items-center justify-center`}>
                    <Icon className={`h-5 w-5 ${color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Main dashboard grid */}
      {(isVisible('staff_hours') || isVisible('availability') || isVisible('coverage')) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {isVisible('staff_hours') && <StaffHoursWidget data={staffUtil} />}
          {isVisible('availability') && <AvailabilityWidget data={staffUtil} />}
          {isVisible('coverage') && <ServiceCoverageWidget data={serviceCov} />}
        </div>
      )}

      {/* Weekly Gaps — full-width row */}
      {isVisible('gaps') && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <WeeklyGapsWidget api={api} weekOffset={weekOffset} />
        </div>
      )}

      {/* Billing projection */}
      {(isVisible('billing') || isVisible('rates')) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {isVisible('billing') && <BillingProjectionWidget api={api} weekOffset={weekOffset} />}
          {isVisible('rates') && (
        <Card className="border-[#DED8EB] shadow-sm bg-white" data-testid="billing-rates-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#2D1B4E] flex items-center gap-2">
              <Clock className="h-4 w-4 text-[#6B3FA0]" /> Unit Rates (15-min)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { label: 'Explore Program', rate: '$6.32', note: 'per unit per participant', color: 'bg-[#9B7ED8]' },
              { label: 'Employment', rate: '$20.60', note: 'per unit', color: 'bg-[#FFB74D]' },
              { label: 'IHCS', rate: '$11.10', note: 'per unit', color: 'bg-[#81C784]' },
            ].map(r => (
              <div key={r.label} className="flex items-center justify-between p-2.5 rounded-lg bg-[#F7F4EF] border border-[#DED8EB]">
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-sm ${r.color}`} />
                  <span className="text-sm font-medium text-[#2D1B4E]">{r.label}</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-bold text-[#2D1B4E]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{r.rate}</span>
                  <p className="text-[10px] text-[#8E85A0]">{r.note}</p>
                </div>
              </div>
            ))}
            <p className="text-[10px] text-[#8E85A0] text-center pt-1">PA Office of Developmental Programs — Medicaid billing</p>
          </CardContent>
        </Card>
          )}
      </div>
      )}

      {/* Staff Weekly Totals */}
      {isVisible('staff_totals') && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <StaffWeeklyTotalsWidget api={api} weekOffset={weekOffset} />
        </div>
      )}

      {/* Today's schedule */}
      {isVisible('today_schedule') && (
      <Card className="border-[#DED8EB] shadow-sm bg-white" data-testid="today-shifts">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-semibold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>Today's Schedule</CardTitle>
            <div className="flex items-center gap-2">
              {Object.entries(SERVICE_LABELS).map(([key, label]) => {
                const count = todayShifts.filter(s => s.service_line === key).length;
                return count > 0 ? (
                  <Badge key={key} className={`text-[10px] ${SERVICE_COLORS[key]?.bg} ${SERVICE_COLORS[key]?.text} ${SERVICE_COLORS[key]?.border} border`}>
                    {label}: {count}
                  </Badge>
                ) : null;
              })}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {todayShifts.length === 0 ? (
            <div className="text-center py-8 text-[#8E85A0]">
              <Calendar className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No shifts scheduled for today</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {sortedTodayShifts.map((shift) => {
                const colors = SERVICE_COLORS[shift.service_line] || {};
                return (
                  <div key={shift.id} className={`flex items-center gap-4 p-3 rounded-lg border transition-all duration-200 hover:-translate-y-px ${shift.has_conflict ? 'border-[#F4A261] bg-orange-50' : `${colors.border} ${colors.bg}/30`}`} data-testid={`shift-${shift.id}`}>
                    <div className="flex-shrink-0">
                      <span className="text-sm font-mono font-medium text-[#2D1B4E]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{formatTime(shift.time_in)}</span>
                      <span className="text-[#8E85A0] mx-1">-</span>
                      <span className="text-sm font-mono font-medium text-[#2D1B4E]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{formatTime(shift.time_out)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#2D1B4E] truncate">
                        {shift.employee_name || <span className="text-[#E65100] italic">Needs staff</span>}
                      </p>
                      {(shift.participants || []).length > 0 && <p className="text-xs text-[#8E85A0] truncate">{shift.participants.join(', ')}</p>}
                    </div>
                    <Badge className={`text-[10px] shrink-0 ${colors.bg} ${colors.text} ${colors.border} border`}>
                      {SERVICE_LABELS[shift.service_line] || shift.service_line}
                    </Badge>
                    {shift.location && <span className="text-xs text-[#8E85A0] shrink-0 flex items-center gap-0.5"><MapPin className="h-3 w-3" />{shift.location}</span>}
                    {shift.has_conflict && <AlertTriangle className="h-4 w-4 text-[#F4A261] shrink-0" />}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
